import React, { useEffect, useRef } from 'react';

interface MemberFamilyDialogProps {
  /** 'dialog' for a neutral action, 'alertdialog' for a confirmation with a safe/unsafe choice. */
  role?: 'dialog' | 'alertdialog';
  titleId: string;
  title: string;
  descriptionId?: string;
  busy?: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Width class, so forms (wider) and confirms (narrower) can differ. Defaults to a confirm-sized dialog. */
  widthClassName?: string;
}

/**
 * Shared accessible dialog shell for the member family-request tools (#127
 * slice 5), matching the admin family tools' shell
 * (`frontend/src/components/admin/family/FamilyDialog.tsx`) exactly:
 * role=dialog/alertdialog, labelled by the title, Escape closes (unless
 * busy), and focus starts on the dialog so a screen reader announces it
 * immediately.
 */
const MemberFamilyDialog: React.FC<MemberFamilyDialogProps> = ({
  role = 'dialog',
  titleId,
  title,
  descriptionId,
  busy,
  onClose,
  children,
  widthClassName = 'max-w-md',
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !busy) onClose();
        }}
        className={`relative w-full ${widthClassName} rounded-xl bg-white p-5 shadow-2xl focus:outline-none max-h-[90vh] overflow-y-auto`}
      >
        <h2 id={titleId} className="text-lg font-bold text-gray-900">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
};

export default MemberFamilyDialog;
